import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Search, UserPlus, CheckCircle, Loader, Shield } from 'lucide-react'
import { usersAPI } from '../services/api'

const ROLE_LABELS = {
  IncidentViewer: 'Incident Viewer',
  IncidentCreator: 'Incident Creator',
  IncidentRequesterEditor: 'Change Requester',
  IncidentImplementorEditor: 'Change Implementor',
  IncidentApproverEditor: 'Change Approvers',
  SupportL1: 'Support L1',
  SupportL2: 'Support L2',
  SupportL3: 'Support L3',
  Admin: 'Admin',
  SuperAdmin: 'Super Admin'
}

export default function UserReboardModal({ isOpen, onClose }) {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(null)
  const [confirmed, setConfirmed] = useState(false)
  const [done, setDone] = useState(null)

  const { data, isLoading } = useQuery({
    queryKey: ['users-inactive', search],
    queryFn: () => usersAPI.getAll({ status: 'Inactive', search, limit: 30 }),
    enabled: isOpen
  })

  const users = (data?.data?.data || []).filter((u) => u.status === 'Inactive')

  const reactivate = useMutation({
    mutationFn: (userId) => usersAPI.updateStatus(userId, { status: 'Active' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['users-active'] })
      queryClient.invalidateQueries({ queryKey: ['users-inactive'] })
      setDone(selected?.name)
      setSelected(null)
      setConfirmed(false)
    }
  })

  const handleClose = () => {
    setSearch('')
    setSelected(null)
    setConfirmed(false)
    setDone(null)
    reactivate.reset()
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
              <UserPlus className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Reboard User</h2>
              <p className="text-xs text-gray-500">Reactivate an offboarded account</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {done && (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
              <p className="font-semibold text-gray-900">{done} has been reboarded.</p>
              <p className="text-sm text-gray-500">Their account is active again and they can sign in.</p>
              <button onClick={handleClose}
                className="mt-2 px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-700 transition-colors">
                Close
              </button>
            </div>
          )}

          {!done && !selected && (
            <>
              <p className="text-sm text-gray-500 mb-4">Search for an inactive (offboarded) user to reactivate.</p>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text" value={search} placeholder="Name or email…"
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-green-400"
                />
              </div>

              {isLoading ? (
                <div className="flex justify-center py-6">
                  <Loader className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : users.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No inactive users found.</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {users.map((u) => {
                    const roles = Array.isArray(u.roles) ? u.roles.map((r) => r?.name || r).filter(Boolean) : []
                    return (
                      <button
                        key={u.id} type="button"
                        onClick={() => setSelected(u)}
                        className="w-full flex items-center gap-3 px-3 py-3 rounded-xl border border-gray-200 hover:border-green-300 hover:bg-green-50 text-left transition-all"
                      >
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center text-white font-bold text-sm shrink-0">
                          {(u.name || 'U')[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{u.name}</p>
                          <p className="text-xs text-gray-500 truncate">{u.email}</p>
                        </div>
                        {roles.length > 0 && (
                          <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium shrink-0">
                            {ROLE_LABELS[roles[0]] || roles[0]}
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
            </>
          )}

          {!done && selected && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-slate-500 to-slate-700 flex items-center justify-center text-white font-bold shrink-0">
                  {(selected.name || 'U')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900">{selected.name}</p>
                  <p className="text-xs text-gray-500">{selected.email}</p>
                  {Array.isArray(selected.roles) && selected.roles.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {selected.roles.map((r) => {
                        const rName = r?.name || r
                        return (
                          <span key={rName} className="inline-flex items-center gap-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium px-1.5 py-0.5 rounded-full">
                            <Shield className="w-2.5 h-2.5" />{ROLE_LABELS[rName] || rName}
                          </span>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>

              <label className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500" />
                <span className="text-sm text-gray-700">
                  I confirm I want to reboard <strong>{selected.name}</strong> and restore their access.
                </span>
              </label>

              {reactivate.isError && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {reactivate.error?.response?.data?.message || 'Failed to reboard user'}
                </p>
              )}
            </div>
          )}
        </div>

        {!done && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl shrink-0">
            <button onClick={selected ? () => { setSelected(null); setConfirmed(false) } : handleClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-200 transition-colors">
              {selected ? '← Back' : 'Cancel'}
            </button>
            {selected && (
              <button
                onClick={() => reactivate.mutate(selected.id)}
                disabled={!confirmed || reactivate.isPending}
                className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {reactivate.isPending
                  ? <><Loader className="w-4 h-4 animate-spin" />Reboarding…</>
                  : <><UserPlus className="w-4 h-4" />Reboard User</>}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
